import React, { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOnClickOutside } from 'usehooks-ts';
import styles from './Tooltip.styles.module.css';

export enum TooltipVariant {
  TOP_RIGHT = 'TOP_RIGHT',
  TOP_LEFT = 'TOP_LEFT',
  TOP_CENTER = 'TOP_CENTER',
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
  BOTTOM_CENTER = 'BOTTOM_CENTER',
}

export enum ToolTipSize {
  REGULAR = 'regular',
  LARGE = 'large',
}

const toolTipSizeClassMap = {
  [ToolTipSize.REGULAR]: styles['tooltip__text-regular'],
  [ToolTipSize.LARGE]: styles['tooltip__text-large'],
};

const tooltipVariantClassMap = {
  [TooltipVariant.TOP_RIGHT]: styles['tooltip__text--top-right'],
  [TooltipVariant.TOP_LEFT]: styles['tooltip__text--top-left'],
  [TooltipVariant.TOP_CENTER]: styles['tooltip__text--top-center'],
  [TooltipVariant.BOTTOM_LEFT]: styles['tooltip__text--bottom-left'],
  [TooltipVariant.BOTTOM_RIGHT]: styles['tooltip__text--bottom-right'],
  [TooltipVariant.BOTTOM_CENTER]: styles['tooltip__text--bottom-center'],
};

interface TooltipProps {
  text: string | JSX.Element;
  children: React.ReactNode;
  showTooltip: boolean;
  variant?: TooltipVariant;
  onClickedOutside: () => void;
  theme?: string;
  size?: ToolTipSize;
  id: string;
  classes?: string;
}

const Tooltip: FC<TooltipProps> = ({
  text,
  children,
  showTooltip,
  variant = TooltipVariant.TOP_LEFT,
  onClickedOutside,
  theme = 'default',
  size = ToolTipSize.REGULAR,
  id,
  classes: className = '',
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const mappedVariantClass = tooltipVariantClassMap[variant];
  const mappedSizeClass = toolTipSizeClassMap[size];
  const visibilityClass = !showTooltip ? styles['tooltip__text--hidden'] : '';

  const classes = [
    styles.tooltip__text,
    visibilityClass,
    mappedVariantClass,
    mappedSizeClass,
    styles[`tooltip-theme--${theme}`],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const updatePosition = () => {
    const anchor = anchorRef.current;
    const tooltip = tooltipRef.current;
    if (!anchor || !tooltip) return;

    const anchorRect = anchor.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const padding = 0;

    let top = 0;
    let left = 0;

    switch (variant) {
      case TooltipVariant.TOP_LEFT:
        top = anchorRect.top - tooltipRect.height - padding;
        left = anchorRect.left - tooltipRect.width;
        break;
      case TooltipVariant.TOP_RIGHT:
        top = anchorRect.top - tooltipRect.height - padding;
        left = anchorRect.right - tooltipRect.width;
        break;
      case TooltipVariant.TOP_CENTER:
        top = anchorRect.top - tooltipRect.height - padding;
        left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
        break;
      case TooltipVariant.BOTTOM_LEFT:
        top = anchorRect.bottom + padding;
        left = anchorRect.left;
        break;
      case TooltipVariant.BOTTOM_RIGHT:
        top = anchorRect.bottom + padding;
        left = anchorRect.right - tooltipRect.width;
        break;
      case TooltipVariant.BOTTOM_CENTER:
        top = anchorRect.bottom + padding;
        left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
        break;
      default:
        top = anchorRect.top - tooltipRect.height - padding;
        left = anchorRect.right - tooltipRect.width;
        break;
    }

    setCoords({ top, left });
  };

  useEffect(() => {
    if (showTooltip) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showTooltip, variant]);

  useOnClickOutside(anchorRef, onClickedOutside);

  return (
    <div className={styles.tooltip} ref={anchorRef} aria-describedby={id}>
      {children}
      {showTooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            className={classes}
            style={{
              top: coords.top,
              left: coords.left,
              position: 'absolute',
              zIndex: 10000,
            }}
            role='tooltip'
            aria-hidden={!showTooltip}
            id={id}
          >
            {text}
          </div>,
          document.body
        )}
    </div>
  );
};

Tooltip.displayName = 'Tooltip';
export default Tooltip;
